/**
 * WasmAudioEngine — H2000's thin wrapper over the dj-daw wasm AudioWorklet engine.
 *
 * Implements IAudioEngine. The wasm engine is the primary path; a small
 * SynthAudioEngine instance is kept as a fallback for:
 *   - Sequence cues (the dj-daw synth CueTable does not yet carry sequences).
 *   - Any cue that fails to convert to the wasm CueTable format.
 *   - Any fire() that happens before the wasm engine has finished booting.
 *
 * 2D spatial is Option B (ratified): H2000 computes pan/gain in TS and passes it
 * per-cue via fire(cueId, { pan, gain }). The engine only applies per-voice pan/gain.
 * setListener() is a reserved no-op placeholder.
 *
 * SAMPLED = P0 in the long term, but this first cutover uses the synth feature of
 * the wasm engine so the existing synth cues keep making sound. The bank loader
 * (loadBank) is wired and ready for a future sampled build.
 */

import type { IAudioEngine, CueLogEntry } from './IAudioEngine';
import type { BusName, Cue, SoundDesc, ToneDesc, NoiseDesc, SampleDesc } from './audioTypes';
import { BUS_NAMES } from './audioTypes';
import { resolveCue, allCues } from '../cues/SoundBank';
import { SynthAudioEngine } from './SynthAudioEngine';
import {
  DjAudioEngine,
  WORKLET_BUS_INDEX,
  type CueTable as WasmCueTable,
} from '../dj-audio-worklet';
import { gameLogger } from '../../../../utils/GameLogger';
import { AudioContextOwner } from './AudioContextOwner';
import { DJ_AUDIO_VERSION } from './djAudioVersion';

const WASM_URL = `/dj-audio/dj_audio_worklet_bg.wasm?v=${DJ_AUDIO_VERSION}`;
const PROCESSOR_URL = `/dj-audio/dj-audio-processor.js?v=${DJ_AUDIO_VERSION}`;
const CUE_LOG_CAP = 64;

interface ProjectSnapshot {
  tempo_bpm: number;
  sample_rate: number;
  tracks: never[];
  buses: Array<{ gain: number; priority: number }>;
}

function buildProject(sampleRate: number): ProjectSnapshot {
  return {
    tempo_bpm: 120.0,
    sample_rate: sampleRate,
    tracks: [],
    buses: [
      { gain: 1.0, priority: 1.0 }, // music (0)
      { gain: 1.0, priority: 1.0 }, // sfx (1)
      { gain: 1.0, priority: 0.8 }, // ambient (2)
      { gain: 1.0, priority: 1.0 }, // ui (3)
      { gain: 1.0, priority: 0.8 }, // spatial (4)
    ],
  };
}

const WASM_OSCS = new Set(['sine', 'square', 'sawtooth', 'triangle']);

function toWasmSoundDesc(sound: SoundDesc): WasmCueTable['cues'][number]['sound'] | null {
  if (sound.kind === 'sequence') return null;
  if (sound.kind === 'sample') {
    const { track, gain } = sound as SampleDesc;
    return { sample: { track }, gain };
  }
  if (sound.kind === 'tone') {
    const { freq, osc, durationMs, gain, sweepTo } = sound as ToneDesc;
    if (!WASM_OSCS.has(osc)) return null;
    return {
      osc: osc as 'sine' | 'square' | 'sawtooth' | 'triangle',
      freq: sweepTo !== undefined ? [freq, sweepTo] : freq,
      durationMs,
      gain,
    };
  }
  const { durationMs, gain, filter } = sound as NoiseDesc;
  if (!filter) return { durationMs, gain };
  const typeToFilter: Record<string, 'lowpass' | 'highpass' | 'bandpass' | 'notch'> = {
    lowpass: 'lowpass',
    highpass: 'highpass',
    bandpass: 'bandpass',
    notch: 'notch',
  };
  const filterKind = typeToFilter[filter.type];
  if (!filterKind) return null;
  return {
    durationMs,
    gain,
    filter: filterKind,
    filterFreq: filter.freq,
  };
}

function toWasmCueTable(cues: Cue[]): WasmCueTable {
  const wasmCues: WasmCueTable['cues'] = [];
  for (const cue of cues) {
    const sound = toWasmSoundDesc(
      Array.isArray(cue.sound) ? cue.sound[0] : cue.sound,
    );
    if (!sound) continue;
    wasmCues.push({
      id: cue.id,
      bus: cue.bus,
      priority: cue.priority,
      sound,
    });
  }
  return { cues: wasmCues };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampPan(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

interface BankIndex {
  schema_version: number;
  game: string;
  version: string;
  cues: Record<string, { url: string; rate: number; loop: boolean; bytes: number; sha: string }>;
}

export class WasmAudioEngine implements IAudioEngine {
  private readonly contextOwner: AudioContextOwner;
  private engine: DjAudioEngine | null = null;
  private bootPromise: Promise<DjAudioEngine | null> | null = null;

  /** Fallback for sequences and pre-boot fires. */
  private synth: SynthAudioEngine;

  /** Requested gains must survive the async worklet boot boundary. */
  private busGainValues: Record<BusName, number> = {
    sfx: 1,
    ui: 1,
    ambient: 1,
    music: 1,
    spatial: 1,
  };

  private cueLog: CueLogEntry[] = [];
  private disposed = false;

  /** Sampled bank loaded via loadBank(): cue ids → Cue + sample file bytes. */
  private bankCues: Cue[] = [];
  private bankSampleFiles: Array<{ track: number; bytes: Uint8Array; looping: boolean; oneShot: boolean }> = [];
  private bankLoaded = false;
  private bankPromise: Promise<void> | null = null;

  constructor(contextOwner = new AudioContextOwner()) {
    this.contextOwner = contextOwner;
    this.synth = new SynthAudioEngine(contextOwner);
  }

  /** Concrete-engine access for the factory handle and legacy BGM bridge. */
  audioContextOwner(): AudioContextOwner {
    return this.contextOwner;
  }

  private async ensureBoot(): Promise<DjAudioEngine | null> {
    if (this.engine) return this.engine;
    if (this.bootPromise) return this.bootPromise;

    this.bootPromise = (async () => {
      if (this.bankPromise) await this.bankPromise.catch(() => undefined);
      if (typeof window === 'undefined') return null;
      const ctx = this.contextOwner.current();
      if (!ctx) return null;
      try {
        const table = toWasmCueTable(this.allCues());
        const opts: Parameters<typeof DjAudioEngine.create>[3] = {
          projectJson: JSON.stringify(buildProject(ctx.sampleRate)),
          cueTable: table,
        };
        if (this.bankSampleFiles.length > 0) {
          opts.sampleFiles = this.bankSampleFiles.map((f) => ({
            track: f.track,
            bytes: f.bytes,
            looping: f.looping,
            oneShot: f.oneShot,
          }));
        }
        const eng = await DjAudioEngine.create(ctx, WASM_URL, PROCESSOR_URL, opts);
        await eng.resume();
        if (this.disposed) {
          eng.dispose();
          return null;
        }
        for (const bus of BUS_NAMES) {
          eng.setBusGain(WORKLET_BUS_INDEX[bus], this.busGainValues[bus]);
        }
        this.engine = eng;
        return eng;
      } catch (e) {
        gameLogger.error('audio', '[WasmAudioEngine] failed to boot DjAudioEngine', e);
        return null;
      }
    })();

    return this.bootPromise;
  }

  private allCues(): Cue[] {
    // Pull every authored cue from the SoundBank plus any bank-loaded sample cues.
    return [...allCues(), ...this.bankCues];
  }

  /** IDs of sampled cues loaded from the bank (empty until loadBank resolves). */
  bankCueIds(): string[] {
    return this.bankCues.map((c) => c.id);
  }

  /** Debug-kit health probe (🔊 window ENGINE strip). Deliberately NOT on
   *  IAudioEngine — the seam stays pristine (the factory-handle precedent);
   *  callers duck-type it. */
  health(): {
    wasmBooted: boolean;
    bankLoaded: boolean;
    disposed: boolean;
    synth: { contextState: AudioContextState | 'none'; muted: boolean; masterVolume: number };
    } {
    return {
      wasmBooted: this.engine !== null,
      bankLoaded: this.bankLoaded,
      disposed: this.disposed,
      synth: this.synth.health(),
    };
  }

  async resume(): Promise<void> {
    if (this.disposed) return;
    await this.synth.resume();
    // Treat the gesture unlock as complete only after the sampled worklet has
    // either acknowledged the versioned bundle or failed into synth fallback.
    // Otherwise GameAudioSystem can flush boot-time sample cues into the brief
    // pre-worklet window, where sample-only cues have no synth realization.
    await this.ensureBoot();
  }

  fire(cueId: string, opts?: { pan?: number; gain?: number; emitterId?: string }): void {
    if (this.disposed) return;
    // A loaded bank is private to this engine instance. Prefer its sample
    // descriptor when WASM is ready; SynthAudioEngine still resolves the
    // static authored cue when this wrapper falls back before/after a failed
    // worklet boot.
    const cue = this.bankCues.find((candidate) => candidate.id === cueId) ?? resolveCue(cueId);
    if (!cue) return;

    const pan = clampPan(opts?.pan ?? 0);
    const gain = clamp01(opts?.gain ?? 1);

    const isSimple = !Array.isArray(cue.sound) && cue.sound.kind !== 'sequence';
    const wasmReady = this.engine !== null;

    if (wasmReady && isSimple) {
      this.engine!.fire(cueId, { pan, gain, emitterId: opts?.emitterId });
      this.logCue(cue, gain, pan, false);
      return;
    }

    // Fallback: sequences or until the wasm engine boots.
    this.synth.fire(cueId, opts);
  }

  private logCue(cue: Cue, gain: number, pan: number, stolen: boolean): void {
    this.cueLog.push({
      t: Date.now(),
      cueId: cue.id,
      bus: cue.bus,
      gain,
      pan,
      priority: cue.priority,
      stolen,
    });
    if (this.cueLog.length > CUE_LOG_CAP) this.cueLog.shift();
  }

  setBusGain(bus: BusName, gain: number): void {
    const g = clamp01(gain);
    this.busGainValues[bus] = g;
    this.synth.setBusGain(bus, g);
    if (this.engine) {
      this.engine.setBusGain(WORKLET_BUS_INDEX[bus], g);
    }
  }

  setListener(_x: number, _y: number): void {
    void _x;
    void _y;
    // Reserved no-op placeholder (future 3D listener).
  }

  drainCueLog(): CueLogEntry[] {
    const synthLogs = this.synth.drainCueLog();
    // DjAudioEngine's raw trace mirrors every wrapper dispatch and timestamps
    // it with AudioContext-relative seconds. Drain it to keep the adapter ring
    // bounded, but expose only the wrapper's canonical wall-clock record with
    // bus, pan, gain, and priority. Appending both produced duplicate QA rows
    // and rendered the raw t=0 entry as an epoch-local time.
    this.engine?.drainCueLog();
    const out = [...this.cueLog, ...synthLogs];
    this.cueLog = [];
    return out;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.synth.dispose();
    this.engine?.dispose();
    this.engine = null;
    void this.contextOwner.close();
  }

  /**
   * Preload a sampled asset bank. Fetches the index, fetches each asset,
   * and hands the bytes to the wasm engine for internal decode.
   *
   * Must be called before resume() so the samples are available at engine boot.
   * If the engine is already booted, this method throws (recreating the engine
   * mid-session is out of scope for the first cutover).
   */
  async loadBank(indexUrl: string): Promise<void> {
    if (this.bankLoaded) {
      return;
    }
    if (this.bankPromise) {
      return this.bankPromise;
    }
    if (typeof window === 'undefined') {
      throw new Error('loadBank() requires a browser environment');
    }

    this.bankPromise = (async () => {
      const indexRes = await fetch(indexUrl);
      if (!indexRes.ok) {
        throw new Error(`failed to fetch bank index: ${indexRes.status} ${indexRes.statusText}`);
      }
      const index: BankIndex = await indexRes.json();
      const baseUrl = indexUrl.substring(0, indexUrl.lastIndexOf('/') + 1);

      const entries = Object.entries(index.cues);
      const files: Array<{ track: number; bytes: Uint8Array; looping: boolean; oneShot: boolean }> = [];
      const bankCues: Cue[] = [];
      let track = 0;

      for (const [cueId, entry] of entries) {
        const url = entry.url.startsWith('http') ? entry.url : `${baseUrl}${entry.url}`;
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`failed to fetch bank asset ${entry.url}: ${res.status} ${res.statusText}`);
        }
        const bytes = new Uint8Array(await res.arrayBuffer());
        const oneShot = !entry.loop;
        files.push({ track, bytes, looping: entry.loop, oneShot });
        bankCues.push({
          id: cueId,
          bus: 'sfx',
          priority: 0.8,
          sound: { kind: 'sample', track, gain: 1.0 },
        });
        track += 1;
      }

      this.bankSampleFiles = files;
      this.bankCues = bankCues;
      this.bankLoaded = true;

      // Do not register these in SoundBank's process-global override map.
      // Multiple explicit debug engines can coexist with the one gameplay
      // engine; an instance-local bank prevents one engine's load/dispose from
      // mutating another engine's synth fallback behavior.
    })();

    return this.bankPromise;
  }
}
